/**
 * Format API date values for admin UI.
 * Handles ISO strings, timestamps, and Jackson LocalDateTime arrays [y, m, d, h, min, s, nano].
 * Also handles Jackson Instant as epoch-seconds number or [seconds, nanos].
 */
import dayjs from 'dayjs';

export function parseApiDate(value) {
  if (value == null || value === '') return null;

  // Jackson Instant object form (rare)
  if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
    if (value.epochSecond != null || value.seconds != null) {
      const sec = Number(value.epochSecond ?? value.seconds);
      const nano = Number(value.nano ?? value.nanos ?? 0);
      if (Number.isFinite(sec)) {
        return new Date(sec * 1000 + Math.round(nano / 1e6));
      }
    }
    if (value.$date != null) {
      return parseApiDate(value.$date);
    }
  }

  if (Array.isArray(value)) {
    if (value.length < 2) return null;

    // Jackson Instant as timestamp array: [epochSeconds, nanoAdjustment]
    const a0 = Number(value[0]);
    const a1 = Number(value[1]);
    if (
      value.length <= 3
      && Number.isFinite(a0)
      && a0 > 1e9
      && a0 < 1e12
      && (value.length === 1 || Number.isFinite(a1))
    ) {
      const ms = a0 * 1000 + (Number.isFinite(a1) ? Math.round(a1 / 1e6) : 0);
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // LocalDateTime / LocalDate array: [y, m, d, h?, min?, s?, nano?]
    if (value.length < 3) return null;
    const [y, m, d, h = 0, min = 0, s = 0, nano = 0] = value;
    // Guard: year-like first element
    if (!Number.isFinite(y) || y < 1970 || y > 3000) return null;
    const ms = typeof nano === 'number' ? Math.round(nano / 1e6) : 0;
    const date = new Date(y, (m || 1) - 1, d || 1, h, min, s, ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    // Epoch seconds vs milliseconds
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Numeric string epoch
    if (/^\d{10,13}$/.test(trimmed)) {
      return parseApiDate(Number(trimmed));
    }

    // "2025,4,25,19,15,28,779000000" (rare stringified array)
    if (/^\d{4},\d{1,2},\d{1,2}/.test(trimmed)) {
      const parts = trimmed.split(',').map((p) => Number(p));
      return parseApiDate(parts);
    }

    // Legacy "YYYY-MM-DD/HH:mm"
    if (/^\d{4}-\d{2}-\d{2}\/\d{2}:\d{2}/.test(trimmed)) {
      const normalized = trimmed.replace('/', 'T');
      const d = dayjs(normalized);
      return d.isValid() ? d.toDate() : null;
    }

    const d = dayjs(trimmed);
    return d.isValid() ? d.toDate() : null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const d = dayjs(value);
  return d.isValid() ? d.toDate() : null;
}

/**
 * @param {*} value
 * @param {string} [pattern='DD.MM.YYYY HH:mm']
 * @returns {string} formatted date or '—'
 */
export function formatApiDate(value, pattern = 'DD.MM.YYYY HH:mm') {
  const date = parseApiDate(value);
  if (!date) return '—';
  return dayjs(date).format(pattern);
}
